import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {BdCalculationItemsDto, BdMessagesDto} from '../../../model/dto/runtime';
import {BdFreedAmountPositionDto} from '../../../model/dto/runtime/bd-calculation-item.dto';
import {
    BdReceivableDescriptor,
    describeReceivable
} from '../../../util/bd-receivable-amount.util';

/**
 * Shared component for displaying balance calculations and validation messages.
 * Used by HandOver, PaymentPlan, and PaymentDistribution object components.
 */
@Component({
    selector: 'dunning-balance-display',
    templateUrl: './dunning-balance-display.component.html',
    standalone: false
})
export class DunningBalanceDisplayComponent implements OnChanges {

    @Input() calculationItems: BdCalculationItemsDto | undefined;
    @Input() messages: BdMessagesDto | undefined;
    @Input() calculated: boolean = false;
    @Input() validated: boolean = false;

    /**
     * Label for the end balance row. Defaults to "Endsaldo:" so the
     * payment-plan/payment-distribution consumers keep their current behavior.
     * The hand-over consumer overrides this with "Inkasso-Übergabesumme:".
     */
    @Input() endBalanceLabel: string = 'Endsaldo:';

    /**
     * U01: Vorzeichen-Quelle der angezeigten Beträge.
     *
     * Die Inkasso-Übergabe (Handover) liefert die Salden im ROHEN KONTO-Vorzeichen,
     * wo **negativ = Forderung (Schuld)** und **positiv = Guthaben** gilt — also
     * GENAU UMGEKEHRT zur modulweiten Cockpit/Worklist-Konvention 'offen = positiv',
     * die {@link describeReceivable} erwartet. Setzt der Handover-Consumer dieses
     * Flag auf {@code true}, werden die Beträge VOR der Klassifizierung negiert
     * (ein rohes -525 ist eine Forderung von +525). Andere Consumer
     * (Zahlungsplan/Verteilung) lassen das Flag auf {@code false}.
     */
    @Input() rawAccountSign: boolean = false;

    /**
     * TM-1000001930: Freigewordene Storno-Beträge (Einigungsgebühr / Kulanz /
     * offener Rest), die der Consumer aus seinem Runtime-/Saldo-DTO durchreicht.
     * Werden als eigener Block unter den Berechnungspositionen dargestellt.
     * Defensiv lesen — leer/null, wenn keine Stornierungen vorliegen.
     */
    @Input() freedAmountPositions: BdFreedAmountPositionDto[] | null = null;

    // Grouped items for display
    groupedItems: Map<string, any[]> = new Map();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['calculationItems'] && this.calculationItems) {
            this.groupItems();
        }
    }

    /**
     * Groups calculation items by object type for organized display.
     */
    private groupItems(): void {
        this.groupedItems.clear();

        if (!this.calculationItems?.items) {
            return;
        }

        for (const item of this.calculationItems.items) {
            const type = this.getDisplayType(item.objectType);
            if (!this.groupedItems.has(type)) {
                this.groupedItems.set(type, []);
            }
            this.groupedItems.get(type)!.push(item);
        }
    }

    /**
     * Gets the German display-friendly group name for a reference object type.
     *
     * TM-1000001962: robust gegen BEIDE Backend-Serialisierungen des
     * BdReferenceObjectType-Enums — numerischer ORDINAL (Standard: das Backend
     * liefert objectType als Ordinal, z. B. 1=CHARGE, 2=DUNNING_CONTRACT,
     * 3=MANUAL_TRANSACTION) UND String-Enum-Name ('CHARGE'…). Der Lookup-Key
     * wird per toString() normalisiert; beide Schlüsselarten zeigen auf dasselbe
     * deutsche Label. Ordinalreihenfolge gegen das Java-Enum verifiziert:
     * 0=TRANSACTION, 1=CHARGE, 2=DUNNING_CONTRACT, 3=MANUAL_TRANSACTION,
     * 4=HAND_OVER, 5=ONE_TIME_PAYMENT, 6=PAYMENT_PLAN.
     */
    getDisplayType(objectType: string | number): string {
        const typeMap: { [key: string]: string } = {
            // String-Enum-Namen
            'TRANSACTION': 'Transaktionen',
            'CHARGE': 'Gebühren',
            'DUNNING_CONTRACT': 'Verträge',
            'MANUAL_TRANSACTION': 'Manuelle Buchungen',
            'ONE_TIME_PAYMENT': 'Einmalzahlungen',
            // TM-1000001974: Storno-Ausgleich (Ausgleich Vertragsstornos) der Inkasso-Übergabe.
            'STORNO_AUSGLEICH': 'Ausgleich Vertragsstornos',
            // Numerische Ordinals (Java-Enum-Deklarationsreihenfolge)
            '0': 'Transaktionen',
            '1': 'Gebühren',
            '2': 'Verträge',
            '3': 'Manuelle Buchungen',
            '5': 'Einmalzahlungen',
            // TM-1000001974: Ordinal 7 = STORNO_AUSGLEICH (Ausgleich Vertragsstornos).
            '7': 'Ausgleich Vertragsstornos'
        };
        // TM-1000001962: Key normalisieren, damit number-Ordinal wie String greift
        const key = objectType?.toString();
        return (key != null && typeMap[key]) || key || '';
    }

    /**
     * Gets the German plain-text title for a validation message type.
     * Keys match the backend BdMessageType enum.
     */
    getMessageTypeLabel(type: string | undefined): string {
        if (!type) {
            return '';
        }
        const typeMap: { [key: string]: string } = {
            'MISSING': 'Fehlende Angabe',
            'WRONG': 'Fehlerhafte Angabe'
        };
        return typeMap[type] || type;
    }

    /**
     * U01: Vorzeichen-/Bedeutungs-Konvention (offen = positiv).
     *
     * Der Endsaldo (z. B. die Inkasso-Übergabesumme) ist unter der modulweiten
     * Konvention eine FORDERUNG, wenn er positiv ist (der Kunde schuldet → rot),
     * und ein GUTHABEN, wenn er negativ ist (grün). Der Magnitude wird stets
     * positiv ausgewiesen; die Bedeutung steckt im Label (Forderung/Guthaben).
     */
    get endBalanceDescriptor(): BdReceivableDescriptor {
        return describeReceivable(
            this.toOpenPositiveConvention(this.calculationItems?.endAmount),
            this.currentCurrency
        );
    }

    /** U01: Deskriptor für einen einzelnen Posten der Aufschlüsselung. */
    describeItemAmount(amount: number | undefined, currency?: string): BdReceivableDescriptor {
        return describeReceivable(
            this.toOpenPositiveConvention(amount),
            currency || this.currentCurrency
        );
    }

    /**
     * R2-5: Liefert den anzuzeigenden Positions-Betrag in der Konvention
     * 'offen = positiv' (= Forderungs-Wirkung der Zeile).
     *
     * Bevorzugt das vom Backend gelieferte {@link BdCalculationItemDto.receivableEffect},
     * dessen Vorzeichen bereits zur Bewegung der angezeigten Gesamtsumme passt
     * (eine Belastung, die die Forderung erhöht, ist positiv). Fehlt der Wert
     * (älteres Backend), fällt die Methode auf das rohe {@code amount} zurück,
     * normalisiert auf 'offen = positiv'. So zeigt eine Kulanz-Belastung kein
     * widersprüchliches Minus mehr, wenn die Übergabesumme steigt.
     */
    itemReceivableEffect(item: { amount?: number; receivableEffect?: number | null }): number | undefined | null {
        if (item?.receivableEffect != null) {
            return item.receivableEffect;
        }
        return this.toOpenPositiveConvention(item?.amount);
    }

    /** R2-5: Formatierter Positions-Betrag nach Forderungs-Wirkung (Magnitude mit Vorzeichen). */
    formatItemEffect(item: { amount?: number; receivableEffect?: number | null; currency?: string }): string {
        return this.formatAmount(this.itemReceivableEffect(item) ?? undefined, item?.currency);
    }

    /** R2-5: Farbklasse einer Buchungszeile nach Forderungs-Wirkung (positiv = rot/Forderung). */
    itemEffectColorClass(item: { amount?: number; receivableEffect?: number | null }): string {
        const effect = this.itemReceivableEffect(item) ?? 0;
        if (effect > 0) {
            return 'text-red-600';
        }
        if (effect < 0) {
            return 'text-green-600';
        }
        return 'text-color-secondary';
    }

    /**
     * U01: Farbklasse für einen einzelnen Posten der Aufschlüsselung. Nach
     * Normalisierung auf 'offen = positiv' gilt: positiv erhöht die Forderung
     * (rot), negativ mindert sie (grün), 0 neutral.
     */
    itemAmountColorClass(amount: number | undefined | null): string {
        const normalized = this.toOpenPositiveConvention(amount) ?? 0;
        if (normalized > 0) {
            return 'text-red-600';
        }
        if (normalized < 0) {
            return 'text-green-600';
        }
        return 'text-color-secondary';
    }

    /**
     * U01: Normalisiert einen Saldo-Betrag auf die Konvention 'offen = positiv'.
     * Bei {@link rawAccountSign} (Handover) wird der rohe Konto-Vorzeichen-Wert
     * negiert (rohes -525 = Forderung +525); sonst bleibt der Wert unverändert.
     */
    private toOpenPositiveConvention(amount: number | undefined | null): number | undefined | null {
        if (amount == null) {
            return amount;
        }
        return this.rawAccountSign ? -amount : amount;
    }

    /** Aktiver Währungscode der Berechnung (Fallback EUR). */
    private get currentCurrency(): string {
        const items = this.calculationItems?.items;
        if (items && items.length > 0 && items[0].currency) {
            return items[0].currency;
        }
        return 'EUR';
    }

    // ----------------------------------------------------------------------
    // TM-1000001930: Freigewordene Storno-Beträge als zusätzliche Anzeige-Zeilen.
    // ----------------------------------------------------------------------

    /** Defensiv aufbereitete Liste der freigewordenen Storno-Positionen. */
    get freedPositions(): BdFreedAmountPositionDto[] {
        return this.freedAmountPositions ?? [];
    }

    /** true, wenn freigewordene Storno-Positionen anzuzeigen sind. */
    get hasFreedPositions(): boolean {
        return this.freedPositions.length > 0;
    }

    /**
     * true, wenn die Position saldowirksam ist (geht in den Gesamtsaldo ein).
     * Nur AGREEMENT_FEE ist typischerweise saldowirksam; KULANZ und
     * OPEN_REMAINDER sind saldoneutral.
     */
    isFreedPositionSaldoRelevant(position: BdFreedAmountPositionDto): boolean {
        return position.kind === 'AGREEMENT_FEE' && position.calculationRelevant === true;
    }

    /** Theme-Textfarbe einer Storno-Position: saldowirksam rot, saldoneutral neutral. */
    freedPositionColorClass(position: BdFreedAmountPositionDto): string {
        return this.isFreedPositionSaldoRelevant(position) ? 'text-red-600' : 'text-color-secondary';
    }

    /**
     * Gets highest severity class for message styling.
     */
    getSeverityClass(severity: string): string {
        switch (severity) {
            case 'ERROR':
                return 'text-red-600 bg-red-50';
            case 'WARNING':
                return 'text-yellow-600 bg-yellow-50';
            case 'INFO':
                return 'text-blue-600 bg-blue-50';
            default:
                return '';
        }
    }

    /**
     * Formats currency amount for display.
     */
    formatAmount(amount: number | undefined, currency?: string): string {
        if (amount === undefined || amount === null) {
            return '-';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(amount);
    }
}
